import MomentUtils from "@date-io/moment";
import { Tooltip } from "@material-ui/core";
import { AddOutlined } from "@material-ui/icons";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { formatToLT } from "./operationUtils";
import { isSingleOperationTaken } from "../../../constants";
import { fmt3 } from "../../../utils";
import { useRef } from "react";

// The picker fires onChange on every keystroke; hold the typed date and report it once
// (calendar accept, blur or Enter) so the date-change prompt doesn't interrupt typing.
const CommitDateTimePicker = ({ onChange, ...props }) => {
  const draft = useRef();
  const commit = (date) => {
    draft.current = undefined;
    if (date ? !date.isValid() : !props.value) return;
    if (date && props.value && date.isSame(props.value, "minute")) return;
    onChange(date);
  };
  const flush = () => draft.current !== undefined && commit(draft.current);
  return (
    <KeyboardDateTimePicker
      {...props}
      onChange={(date) => {
        draft.current = date;
      }}
      onAccept={commit}
      onBlur={flush}
      onKeyDown={(e) => e.key === "Enter" && flush()}
    />
  );
};

const FualSupplyTable = ({
  handleDateChange,
  data = [],
  handleInputChanges,
  onAddRow,
  onDeleteRow,
  onPortChange,
  isReadOnly = false,
  lockFirstRow = false,
}) => {
  const formatVal = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    if (typeof val === 'number') return fmt3(val);
    return val;
  };

  const handleBlurFormat = (index, field, val) => {
    if (val !== "" && val !== null && val !== undefined && !isNaN(Number(val))) {
      handleInputChanges(index, field, fmt3(val));
    }
  };
  return (
    <div className="w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden border-none rounded-none bg-white scroll-thin pb-0 shadow-none">
      <table className="w-full min-w-full border-collapse text-[11px]">
        <thead>
          {/* Group Header Row */}
          <tr className="bg-[#313C4C] text-white h-[24px]">
            {!isReadOnly && (
              <th className="w-[24px] min-w-[24px] border-r border-b border-[#5b6779] p-0 align-middle"></th>
            )}
            <th colSpan="3" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Itinerary
            </th>
            <th colSpan="2" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Arrival
            </th>
            <th colSpan="3" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Arrival Qty
            </th>
            <th colSpan="3" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Supply Qty
            </th>
            <th colSpan="2" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Reserve & Paid
            </th>
            <th colSpan="3" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Supply Prices
            </th>
            <th colSpan="2" className="border-r border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize">
              Departure
            </th>
            <th colSpan="3" className={`${!isReadOnly ? 'border-r' : ''} border-b border-[#5b6779] px-1 py-0.5 align-middle font-medium text-[11px] text-center capitalize`}>
              Departure Qty
            </th>
            {!isReadOnly && (
              <th className="w-[24px] min-w-[24px] border-b border-[#5b6779] p-0 align-middle"></th>
            )}
          </tr>

          {/* Sub Header Row */}
          <tr className="bg-[#313C4C] text-white h-[24px]">
            {!isReadOnly && (
              <th className="w-[24px] min-w-[24px] text-center border-r border-b border-[#5b6779] px-0.5 py-0.5 align-middle font-medium text-[11px] h-[24px]">
                <span className="sr-only">Add</span>
              </th>
            )}
            <th className="px-1.5 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[105px] min-w-[105px]">
              Operation
            </th>
            <th className="px-1.5 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[105px] min-w-[105px]">
              Port
            </th>
            <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[70px]">
              Time Zone (GMT)
            </th>

            <Tooltip title="Arrival Time (Local Time)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[122px]">
                Arrival (LT)
              </th>
            </Tooltip>

            <Tooltip title="Arrival Time (Zulu Time)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Arrival (Z)
              </th>
            </Tooltip>

            <Tooltip title="Arrival Quantity Main Fuel (HSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Main Fuel
              </th>
            </Tooltip>
            <Tooltip title="Arrival Quantity ECA Fuel (LSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                ECA Fuel
              </th>
            </Tooltip>
            <Tooltip title="Arrival Quantity MGO" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                MGO
              </th>
            </Tooltip>

            <Tooltip title="Supply Quantity Main Fuel (HSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Main Fuel
              </th>
            </Tooltip>
            <Tooltip title="Supply Quantity ECA Fuel (LSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                ECA Fuel
              </th>
            </Tooltip>
            <Tooltip title="Supply Quantity MGO" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                MGO
              </th>
            </Tooltip>

            <th className="min-w-[82px] px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
              Trip Reserve
            </th>
            <th className="min-w-[82px] px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
              Paid By
            </th>

            <Tooltip title="Supply Prices Main Fuel (HSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Main Fuel
              </th>
            </Tooltip>
            <Tooltip title="Supply Prices ECA Fuel (LSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                ECA Fuel
              </th>
            </Tooltip>
            <Tooltip title="Supply Prices MGO" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                MGO
              </th>
            </Tooltip>

            <Tooltip title="Departure Time (Local Time)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px] w-[122px]">
                Departure (LT)
              </th>
            </Tooltip>
            <Tooltip title="Departure Time (Zulu Time)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Departure (Z)
              </th>
            </Tooltip>

            <Tooltip title="Departure Quantity Main Fuel (HSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                Main
              </th>
            </Tooltip>
            <Tooltip title="Departure Quantity ECA Fuel (LSFO)" placement="top" arrow>
              <th className="px-1 py-0.5 text-[11px] align-middle font-medium border-r border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]">
                ECA
              </th>
            </Tooltip>
            <Tooltip title="Departure Quantity MGO" placement="top" arrow>
              <th className={`px-1 py-0.5 text-[11px] align-middle font-medium ${!isReadOnly ? 'border-r' : ''} border-b border-[#5b6779] text-left whitespace-nowrap text-white capitalize h-[24px]`}>
                MGO
              </th>
            </Tooltip>

            {!isReadOnly && (
              <th className="w-[24px] min-w-[24px] text-center border-b border-[#5b6779] px-0.5 py-0.5 align-middle font-medium text-[11px] h-[24px]">
                <span className="sr-only">Delete</span>
              </th>
            )}
          </tr>
        </thead>

        <tbody className="bg-white divide-y-0">
          {data?.cargoList?.map((row, index) => {
            const fuelPriceMap = {
              TYPE0: {
                fuelPriceType0Hsfo: row?.cargoDetails?.fuelPriceType0Hsfo || 0,
                fuelPriceType0Lsfo: row?.cargoDetails?.fuelPriceType0Lsfo || 0,
                fuelPriceType0Mgo: row?.cargoDetails?.fuelPriceType0Mgo || 0,
              },
              TYPE1: {
                fuelPriceType1Hsfo: index === 0 ? row?.cargoDetails?.bnkPriceDeliveryhs || 0 : row?.cargoDetails?.fuelPriceType1Hsfo || 0,
                fuelPriceType1Lsfo: index === 0 ? row?.cargoDetails?.bnkPriceDeliveryls || 0 : row?.cargoDetails?.fuelPriceType1Lsfo || 0,
                fuelPriceType1Mgo: index === 0 ? row?.cargoDetails?.bnkPriceDeliverymgo || 0 : row?.cargoDetails?.fuelPriceType1Mgo || 0,
              },
              TYPE2: {
                fuelPriceType2Hsfo: row?.cargoDetails?.fuelPriceType2Hsfo || 0,
                fuelPriceType2Lsfo: row?.cargoDetails?.fuelPriceType2Lsfo || 0,
                fuelPriceType2Mgo: row?.cargoDetails?.fuelPriceType2Mgo || 0,
              },
              TYPE3: {
                fuelPriceType3Hsfo: row?.cargoDetails?.fuelPriceType3Hsfo || 0,
                fuelPriceType3Lsfo: row?.cargoDetails?.fuelPriceType3Lsfo || 0,
                fuelPriceType3Mgo: row?.cargoDetails?.fuelPriceType3Mgo || 0,
              },
              TYPE4: {
                fuelPriceType4Hsfo: row?.cargoDetails?.fuelPriceType4Hsfo || 0,
                fuelPriceType4Lsfo: row?.cargoDetails?.fuelPriceType4Lsfo || 0,
                fuelPriceType4Mgo: row?.cargoDetails?.fuelPriceType4Mgo || 0,
              },
            };
            const fuelType = data?.fuelType || "TYPE0";
            const fuelPrices = fuelPriceMap[fuelType] || {
              fuelPriceType0Hsfo: row?.cargoDetails?.fuelPriceType0Hsfo || 0,
              fuelPriceType0Lsfo: row?.cargoDetails?.fuelPriceType0Lsfo || 0,
              fuelPriceType0Mgo: row?.cargoDetails?.fuelPriceType0Mgo || 0,
            };

            const opBg = (index === 0 && !isReadOnly) ? "bg-[#f1f5f9]" : "bg-[#ffffd6]";

            return (
              <tr
                key={index}
                {...(lockFirstRow && index === 0 ? { inert: "" } : {})}
                className={`h-[19px] transition-colors ${lockFirstRow && index === 0 ? "[&_td]:!bg-[#f1f5f9] opacity-70" : "hover:bg-amber-50/50"}`}
              >
                {!isReadOnly && (
                  <td className="border-r border-b border-[#cbd5e1] text-center p-0 align-middle w-[24px] h-[19px] bg-white">
                    <Tooltip title="Add Row" placement="left" arrow>
                      <button
                        type="button"
                        onClick={() => onAddRow(index)}
                        className="w-full h-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer p-0"
                      >
                        <AddOutlined className="!text-[13px]" />
                      </button>
                    </Tooltip>
                  </td>
                )}

                {/* Operation */}
                <td className={`border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] whitespace-nowrap w-[105px] min-w-[105px] ${opBg}`}>
                  <Tooltip title={row.operation || ""} placement="top" arrow>
                    <select
                      className="cargo-table-select w-full h-[19px] text-[11px] font-medium text-black bg-transparent border-none outline-none px-0.5 py-0 cursor-pointer capitalize disabled:bg-[#f1f5f9] disabled:text-black leading-[19px] text-left"
                      value={row.operation || ""}
                      disabled={isReadOnly || index === 0}
                      onChange={(e) => {
                        onPortChange(index, "operation", e.target.value);
                      }}
                    >
                      <option value="Commence" disabled={index > 0} hidden={index > 0} className="capitalize">Commence</option>
                      <option value="Loading" className="capitalize">Loading</option>
                      <option value="Passing" className="capitalize">Passing</option>
                      <option value="Delivery-out" disabled={isSingleOperationTaken(data?.cargoList, "Delivery-out") && row.operation !== "Delivery-out"} className="capitalize">Delivery-out</option>
                      <option value="Redelivery-in" disabled={isSingleOperationTaken(data?.cargoList, "Redelivery-in") && row.operation !== "Redelivery-in"} className="capitalize">Redelivery-in</option>
                      <option value="Redelivery-out" className="capitalize">Redelivery-out</option>
                      <option value="Waiting" className="capitalize">Waiting</option>
                      <option value="Bunkering" className="capitalize">Bunkering</option>
                      <option value="Redelivery" className="capitalize">Redelivery</option>
                      <option value="Relay" className="capitalize">Relay</option>
                      <option value="Discharging" className="capitalize">Discharging</option>
                    </select>
                  </Tooltip>
                </td>

                {/* Port */}
                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap w-[105px] min-w-[105px]">
                  <Tooltip title={row.port || "Empty"} placement="top" arrow>
                    <span className="truncate block w-full px-1.5 font-medium text-black capitalize leading-[19px] text-left">
                      {row.port}
                    </span>
                  </Tooltip>
                </td>

                {/* Time Zone */}
                <td className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap w-[70px]">
                  <Tooltip title={"GMT " + (row.timeZone || "0")} placement="top" arrow>
                    <input
                      type="text"
                      className="cargo-table-input w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 font-medium leading-[19px] text-left"
                      value={row.timeZone || 0}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          value === "+" ||
                          value === "-" ||
                          value === "+." ||
                          value === "-." ||
                          (/^[+-]?\d*\.?\d*$/.test(value) &&
                            value !== "." &&
                            (value === "" ||
                              value === "+" ||
                              value === "-" ||
                              (parseFloat(value) >= -23 &&
                                parseFloat(value) <= 23)))
                        ) {
                          handleInputChanges(index, "timeZone", value);
                        }
                      }}
                    />
                  </Tooltip>
                </td>

                {/* Arrival Times (LT and Z) */}
                {["arrivalTime", "arrivalTimeZ"].map((field) => {
                  const isRead = field === "arrivalTimeZ" || isReadOnly;
                  if (isRead) {
                    const displayDate = formatToLT(row[field]) || "";
                    return (
                      <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                        <Tooltip title={displayDate || "Empty"} placement="top" arrow>
                          <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                            {displayDate}
                          </span>
                        </Tooltip>
                      </td>
                    );
                  }
                  return (
                    <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap">
                      <MuiPickersUtilsProvider utils={MomentUtils}>
                        <Tooltip
                          title={row[field] ? `Selected: ${row[field]}` : "Select date and time"}
                          placement="top"
                          arrow
                        >
                          <div className="flex items-center w-[122px] h-full px-0.5">
                            <CommitDateTimePicker
                              size="small"
                              className="laytime-datepicker w-full bg-transparent h-[18px] text-[11px] text-black !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[18px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[18px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[18px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-0.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[18px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-black [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[18px] [&_.MuiInputAdornment-root]:!p-0 [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[12px] [&_.MuiIconButton-root]:!w-[12px] [&_.MuiIconButton-root]:!min-w-[12px] [&_.MuiIconButton-root]:!min-h-[12px] [&_.MuiIconButton-root]:!bg-transparent [&_.MuiSvgIcon-root]:!w-2.5 [&_.MuiSvgIcon-root]:!h-2.5 [&_.MuiSvgIcon-root]:!text-[9px]"
                              format="DD/MM/YYYY HH:mm"
                              value={
                                row[field]
                                  ? (moment(row[field], ["DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss.S", "YYYY-MM-DD HH:mm", "DD-MM-YYYY HH:mm", moment.ISO_8601]).isValid()
                                    ? moment(row[field], ["DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss.S", "YYYY-MM-DD HH:mm", "DD-MM-YYYY HH:mm", moment.ISO_8601])
                                    : null)
                                  : null
                              }
                              onChange={(date) => {
                                const formattedDate = date
                                  ? date.format("DD/MM/YYYY HH:mm")
                                  : null;
                                handleDateChange(index, field, formattedDate);
                              }}
                              inputProps={{
                                placeholder: "DD/MM/YYYY",
                                style: {
                                  fontSize: "11px",
                                  fontWeight: "normal",
                                  padding: "0px",
                                  height: "18px",
                                  color: "#1e293b",
                                  lineHeight: "18px",
                                },
                              }}
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: "11px",
                                  fontWeight: "normal",
                                  paddingRight: "0px",
                                  padding: "0px",
                                  marginTop: "0px",
                                  height: "18px",
                                },
                              }}
                              keyboardIcon={
                                <CalendarTodayIcon
                                  style={{
                                    fontSize: "0.55rem",
                                    color: "#64748b",
                                  }}
                                />
                              }
                              InputAdornmentProps={{
                                position: "end",
                                style: { margin: 0, padding: 0 },
                              }}
                              KeyboardButtonProps={{
                                style: {
                                  padding: 0,
                                  margin: 0,
                                  width: "12px",
                                  height: "12px",
                                  minWidth: "12px",
                                },
                                title: "Select date and time",
                              }}
                              disableMaskedInput={false}
                              error={false}
                              helperText={null}
                              FormHelperTextProps={{ style: { display: "none" } }}
                              onError={() => { }}
                            />
                          </div>
                        </Tooltip>
                      </MuiPickersUtilsProvider>
                    </td>
                  );
                })}

                {/* Arrival Quantities */}
                {["arrivalHsfo", "arrivalLsfo", "arrivalMgo"].map((field) => {
                  const val = row[field];
                  const displayVal = formatVal(val);
                  return (
                    <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap">
                      <Tooltip title={displayVal || "Empty"} placement="top" arrow>
                        <input
                          type="text"
                          className="cargo-table-input w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 font-medium leading-[19px] text-left"
                          value={displayVal ?? ""}
                          onChange={(e) => handleInputChanges(index, field, e.target.value)}
                          onBlur={(e) => handleBlurFormat(index, field, e.target.value)}
                        />
                      </Tooltip>
                    </td>
                  );
                })}

                {/* Supply Quantities */}
                {[
                  "bunkerSuppliedHSFO",
                  "bunkerSuppliedLSFO",
                  "bunkerSuppliedMGO",
                ].map((field) => {
                  const val = row?.cargoDetails?.[field];
                  const displayVal = formatVal(val);
                  const locked = lockFirstRow && index === 0;
                  return (
                    <td key={field} className={`border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] ${locked ? "bg-[#f1f5f9]" : "bg-[#ffffd6]"} whitespace-nowrap`}>
                      <Tooltip title={displayVal || "Empty"} placement="top" arrow>
                        <input
                          type="text"
                          disabled={locked}
                          className="cargo-table-input w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 font-medium leading-[19px] text-left"
                          value={displayVal ?? ""}
                          onChange={(e) => handleInputChanges(index, field, e.target.value)}
                          onBlur={(e) => handleBlurFormat(index, field, e.target.value)}
                        />
                      </Tooltip>
                    </td>
                  );
                })}

                {/* Trip Reserve */}
                <td className="min-w-[82px] border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap">
                  <Tooltip title={row?.cargoDetails?.reserve || "Empty"} placement="top" arrow>
                    <select
                      value={
                        row?.cargoDetails?.reserve
                          ? (String(row.cargoDetails.reserve).toLowerCase() === "supply"
                              ? "Supply"
                              : String(row.cargoDetails.reserve).toLowerCase() === "reserve"
                              ? "Reserve"
                              : row.cargoDetails.reserve)
                          : ""
                      }
                      onChange={(e) => handleInputChanges(index, "reserve", e.target.value)}
                      className="cargo-table-select w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 cursor-pointer font-medium capitalize leading-[19px] text-left"
                    >
                      <option value="" disabled className="text-gray-400">Select</option>
                      <option value="Reserve" className="capitalize">Reserve</option>
                      <option value="Supply" className="capitalize">Supply</option>
                    </select>
                  </Tooltip>
                </td>

                {/* Paid By */}
                <td className="min-w-[82px] border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap">
                  <Tooltip title={row?.cargoDetails?.paidBy || "Empty"} placement="top" arrow>
                    <select
                      value={
                        row?.cargoDetails?.paidBy === "External"
                          ? "Operator"
                          : row?.cargoDetails?.paidBy === "Internal"
                          ? "Owner"
                          : (row?.cargoDetails?.paidBy || "")
                      }
                      onChange={(e) => handleInputChanges(index, "paidBy", e.target.value)}
                      className="cargo-table-select w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 cursor-pointer font-medium capitalize leading-[19px] text-left"
                    >
                      <option value="" disabled className="text-gray-400">Select</option>
                      <option value="Operator" className="capitalize">External</option>
                      <option value="Owner" className="capitalize">Internal</option>
                    </select>
                  </Tooltip>
                </td>

                {/* Supply Prices */}
                {Object.keys(fuelPrices || {}).map((field) => {
                  const val = row?.cargoDetails?.[field];
                  const displayVal = formatVal(val);
                  const locked = lockFirstRow && index === 0;
                  return (
                    <td key={field} className={`border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] ${locked ? "bg-[#f1f5f9]" : "bg-[#ffffd6]"} whitespace-nowrap`}>
                      <Tooltip title={displayVal || "Empty"} placement="top" arrow>
                        <input
                          type="text"
                          disabled={locked}
                          className="cargo-table-input w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 font-medium leading-[19px] text-left"
                          value={displayVal ?? ""}
                          onChange={(e) => handleInputChanges(index, field, e.target.value)}
                          onBlur={(e) => handleBlurFormat(index, field, e.target.value)}
                        />
                      </Tooltip>
                    </td>
                  );
                })}

                {/* Departure Times (LT and Z) */}
                {["departureTime", "departureTimeZ"].map((field) => {
                  const isRead = field === "departureTimeZ" || isReadOnly;
                  if (isRead) {
                    const displayDate = formatToLT(row[field]) || "";
                    return (
                      <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#f1f5f9] whitespace-nowrap">
                        <Tooltip title={displayDate || "Empty"} placement="top" arrow>
                          <span className="truncate block w-full px-1 font-medium text-black leading-[19px] text-left">
                            {displayDate}
                          </span>
                        </Tooltip>
                      </td>
                    );
                  }
                  return (
                    <td key={field} className="border-r border-b border-[#cbd5e1] p-0 align-middle h-[19px] bg-[#ffffd6] whitespace-nowrap">
                      <MuiPickersUtilsProvider utils={MomentUtils}>
                        <Tooltip
                          title={row[field] ? `Selected: ${row[field]}` : "Select date and time"}
                          placement="top"
                          arrow
                        >
                          <div className="flex items-center w-[122px] h-full px-0.5">
                            <CommitDateTimePicker
                              size="small"
                              className="laytime-datepicker w-full bg-transparent h-[18px] text-[11px] text-black !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[18px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[18px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[18px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-0.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[18px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-black [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[18px] [&_.MuiInputAdornment-root]:!p-0 [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[12px] [&_.MuiIconButton-root]:!w-[12px] [&_.MuiIconButton-root]:!min-w-[12px] [&_.MuiIconButton-root]:!min-h-[12px] [&_.MuiIconButton-root]:!bg-transparent [&_.MuiSvgIcon-root]:!w-2.5 [&_.MuiSvgIcon-root]:!h-2.5 [&_.MuiSvgIcon-root]:!text-[9px]"
                              format="DD/MM/YYYY HH:mm"
                              value={
                                row[field]
                                  ? (moment(row[field], ["DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss.S", "YYYY-MM-DD HH:mm", "DD-MM-YYYY HH:mm", moment.ISO_8601]).isValid()
                                    ? moment(row[field], ["DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss.S", "YYYY-MM-DD HH:mm", "DD-MM-YYYY HH:mm", moment.ISO_8601])
                                    : null)
                                  : null
                              }
                              onChange={(date) => {
                                const formattedDate = date
                                  ? date.format("DD/MM/YYYY HH:mm")
                                  : null;
                                handleDateChange(index, field, formattedDate);
                              }}
                              inputProps={{
                                placeholder: "DD/MM/YYYY",
                                style: {
                                  fontSize: "11px",
                                  fontWeight: "normal",
                                  padding: "0px",
                                  height: "18px",
                                  color: "#1e293b",
                                  lineHeight: "18px",
                                },
                              }}
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: "11px",
                                  fontWeight: "normal",
                                  paddingRight: "0px",
                                  padding: "0px",
                                  marginTop: "0px",
                                  height: "18px",
                                },
                              }}
                              keyboardIcon={
                                <CalendarTodayIcon
                                  style={{
                                    fontSize: "0.55rem",
                                    color: "#64748b",
                                  }}
                                />
                              }
                              InputAdornmentProps={{
                                position: "end",
                                style: { margin: 0, padding: 0 },
                              }}
                              KeyboardButtonProps={{
                                style: {
                                  padding: 0,
                                  margin: 0,
                                  width: "12px",
                                  height: "12px",
                                  minWidth: "12px",
                                },
                                title: "Select date and time",
                              }}
                              disableMaskedInput={false}
                              error={false}
                              helperText={null}
                              FormHelperTextProps={{ style: { display: "none" } }}
                              onError={() => { }}
                            />
                          </div>
                        </Tooltip>
                      </MuiPickersUtilsProvider>
                    </td>
                  );
                })}

                {/* Departure Quantities */}
                {["departureHsfo", "departureLsfo", "departureMgo"].map((field, fIdx) => {
                  const isLastField = isReadOnly && fIdx === 2;
                  const val = row[field];
                  const displayVal = formatVal(val);
                  const locked = lockFirstRow && index === 0;
                  return (
                    <td key={field} className={`${isLastField ? '' : 'border-r'} border-b border-[#cbd5e1] p-0 align-middle h-[19px] ${locked ? "bg-[#f1f5f9]" : "bg-[#ffffd6]"} whitespace-nowrap`}>
                      <Tooltip title={displayVal || "Empty"} placement="top" arrow>
                        <input
                          type="text"
                          disabled={locked}
                          className="cargo-table-input w-full h-[19px] text-[11px] text-black bg-transparent border-none outline-none px-0.5 py-0 font-medium leading-[19px] text-left"
                          value={displayVal ?? ""}
                          onChange={(e) => handleInputChanges(index, field, e.target.value)}
                          onBlur={(e) => handleBlurFormat(index, field, e.target.value)}
                        />
                      </Tooltip>
                    </td>
                  );
                })}

                {!isReadOnly && (
                  <td className="border-b border-[#cbd5e1] text-center p-0 align-middle w-[24px] h-[19px] bg-white">
                    <Tooltip title="Delete Row" placement="top" arrow>
                      <button
                        type="button"
                        onClick={() => onDeleteRow(index)}
                        disabled={data.length <= 1}
                        className="w-full h-full flex items-center justify-center text-rose-500 hover:text-rose-700 disabled:text-slate-300 hover:bg-rose-50 transition-colors border-none bg-transparent cursor-pointer p-0"
                      >
                        <DeleteOutlineIcon className="!text-[13px]" />
                      </button>
                    </Tooltip>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FualSupplyTable;
